import {
  Component,
  EventEmitter,
  OnDestroy,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { ContentsService } from '@nimmsta/ng-commons-lib/services/contents';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import {
  InputWithErrorComponent,
  SaveableFormComponent,
} from '@nimmsta/ng-commons-lib/forms';
import { WorkflowTemplate } from '@nimmsta/ng-commons-lib/models';
import { HttpClient } from '@angular/common/http';
import {
  DomSanitizer,
  SafeResourceUrl,
  SafeUrl,
} from '@angular/platform-browser';
import { TranslatePipe } from '@ngx-translate/core';
import {
  FileUpload,
  FileUploadHandlerEvent,
  FileUploadModule,
} from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { Subscription, switchMap, tap } from 'rxjs';
import { WorkflowTemplateService } from '../../services/workflow-template.service';
import {
  classifyWorkflowMediaPreview,
  WorkflowMediaPreview,
  WorkflowMediaPreviewType,
} from '../workflow-media-preview.util';

@Component({
  selector: 'app-workflow-template-form',
  templateUrl: 'workflow-template-form.component.html',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    TranslatePipe,
    InputWithErrorComponent,
    ErrorComponent,
    FileUploadModule,
    ButtonModule,
  ],
})
export class WorkflowTemplateFormComponent
  extends SaveableFormComponent<WorkflowTemplate>
  implements OnDestroy
{
  private fb = inject(FormBuilder);
  private contentsService = inject(ContentsService);
  private http = inject(HttpClient);

  @Output() override closeEvent = new EventEmitter();

  @Output() override loadingEvent = new EventEmitter<boolean>();

  protected override dataService = inject(WorkflowTemplateService);
  sanitizer = inject(DomSanitizer);

  @ViewChild(FileUpload) private fileUpload?: FileUpload;

  uploadingFile = false;

  currentFile?: File;

  previewType?: WorkflowMediaPreviewType;

  previewUrl?: SafeUrl;

  previewResourceUrl?: SafeResourceUrl;

  previewSrcDoc?: string;

  private existingFileUrl?: string;
  private currentObjectUrl?: string;
  private htmlPreviewObjectUrl?: string;
  private signedUrlSubscription?: Subscription;
  private embedStringSubscription?: Subscription;

  constructor() {
    super();
    this.setValue({} as WorkflowTemplate);
  }

  override setValue(value: WorkflowTemplate): void {
    this.resetPreviewState();
    super.setValue(value);
    this.subscribeToEmbedStringChanges();
    this.form.patchValue(value);
    this.updatePreview();
    if (value.fileKey) {
      this.signedUrlSubscription = this.contentsService
        .getSignedUrl(value.fileKey)
        .subscribe((signedUrl: string) => {
          this.existingFileUrl = signedUrl;
          this.updatePreview();
        });
    }
  }

  protected setFormGroup(): void {
    this.form = this.fb.group({
      id: this.value.id,
      name: [
        this.value.name,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(255),
        ],
      ],
      description: [this.value.description, [Validators.maxLength(255)]],
      fileKey: [this.value.fileKey],
      embedString: [this.value.embedString],
      createForOrganization: [this.value.createForOrganization],
    });
  }

  onUpload(event: FileUploadHandlerEvent): void {
    if (event.files && event.files.length) {
      const file = event.files[0];
      this.clearCurrentObjectUrl();
      this.currentFile = file;
      this.currentObjectUrl = URL.createObjectURL(file);
      this.updatePreview();
    }
  }

  hasFile(): boolean {
    return !!(this.currentFile || this.form.controls['fileKey'].value);
  }

  removeFile(): void {
    if (!this.editable || this.form.disabled || this.uploadingFile) {
      return;
    }
    this.signedUrlSubscription?.unsubscribe();
    this.currentFile = undefined;
    this.existingFileUrl = undefined;
    this.clearCurrentObjectUrl();
    this.fileUpload?.clear();
    this.form.controls['fileKey'].setValue(null);
    this.form.markAsDirty();
    this.updatePreview();
  }

  override onSave(): void {
    if (this.uploadingFile) {
      return;
    }
    if (this.currentFile) {
      this.uploadingFile = true;
      this.loadingEvent.emit(true);
      this.contentsService
        .getSignedUploadUrl(this.currentFile.name, this.currentFile.type)
        .pipe(
          tap(({ key }) => {
            this.form.controls['fileKey'].setValue(key);
            this.form.markAsDirty();
          }),
          switchMap(({ url }) => this.http.put(url, this.currentFile)),
        )
        .subscribe({
          next: () => {
            this.uploadingFile = false;
            super.onSave();
          },
          error: (error) => {
            this.uploadingFile = false;
            this.loadingEvent.emit(false);
            this.error = error;
          },
        });
    } else {
      super.onSave();
    }
  }

  ngOnDestroy(): void {
    this.resetPreviewState();
  }

  private subscribeToEmbedStringChanges(): void {
    this.embedStringSubscription?.unsubscribe();
    this.embedStringSubscription = this.form.controls[
      'embedString'
    ].valueChanges.subscribe(() => {
      this.updatePreview();
    });
  }

  private updatePreview(): void {
    const preview = classifyWorkflowMediaPreview({
      embedString: <string | null | undefined>(
        this.form.controls['embedString'].value
      ),
      fileUrl: this.currentObjectUrl ?? this.existingFileUrl,
      fileType: this.currentFile?.type,
      fileName: this.currentFile?.name,
      fileKey: <string | null | undefined>this.form.controls['fileKey'].value,
    });
    this.applyPreview(preview);
  }

  private applyPreview(preview: WorkflowMediaPreview | null): void {
    this.clearRenderedPreview();
    if (!preview) {
      return;
    }

    this.previewType = preview.type;
    if (preview.type === 'image' && preview.src) {
      this.previewUrl = this.sanitizer.bypassSecurityTrustUrl(preview.src);
      return;
    }

    if ((preview.type === 'iframe' || preview.type === 'pdf') && preview.src) {
      this.previewResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        preview.src,
      );
      return;
    }

    if (preview.type === 'html' && preview.srcDoc) {
      this.htmlPreviewObjectUrl = URL.createObjectURL(
        new Blob([preview.srcDoc], { type: 'text/html' }),
      );
      this.previewResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.htmlPreviewObjectUrl,
      );
    }
  }

  private resetPreviewState(): void {
    this.signedUrlSubscription?.unsubscribe();
    this.embedStringSubscription?.unsubscribe();
    this.currentFile = undefined;
    this.existingFileUrl = undefined;
    this.clearCurrentObjectUrl();
    this.clearRenderedPreview();
  }

  private clearRenderedPreview(): void {
    this.clearHtmlPreviewObjectUrl();
    this.previewType = undefined;
    this.previewUrl = undefined;
    this.previewResourceUrl = undefined;
    this.previewSrcDoc = undefined;
  }

  private clearCurrentObjectUrl(): void {
    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
      this.currentObjectUrl = undefined;
    }
  }

  private clearHtmlPreviewObjectUrl(): void {
    if (this.htmlPreviewObjectUrl) {
      URL.revokeObjectURL(this.htmlPreviewObjectUrl);
      this.htmlPreviewObjectUrl = undefined;
    }
  }
}
